/**
 * ArticlesFormController — page `articles-edition.html` (création) et
 * `articles-edition.html?id={id}` (édition) (EPIC-010 / 10.4).
 *
 * Pilote la création et l'édition d'un article via les endpoints 10.2 :
 *   - mode `create`  → POST  /articles
 *   - mode `edit`    → PATCH /articles/{id}
 *
 * Sélecteur activité conditionnel :
 *   - 1 seule activité active → champ caché `<input type="hidden">` pré-rempli.
 *   - N > 1 → <select> peuplé depuis `GET /activities` + badge catégorie
 *     (BIC vente / services / BNC) qui suit la valeur sélectionnée.
 *   - Édition d'un article dont l'activité courante est `archived` →
 *     drop-down forcée visible (même si N=1 actives), banner affiché,
 *     valeur courante non sélectionnable.
 *
 * Validation client symétrique au backend (cf. ArticleService::validate*) :
 *   - label : non vide, ≤ 500
 *   - description : ≤ 10000
 *   - unit : non vide, ≤ 50
 *   - priceExclVat : numérique ≥ 0, ≤ 2 décimales (normalisation FR → US)
 *   - vatRate : liste fermée {0, 2.1, 5.5, 10, 20}
 *   - activityId : requis
 *
 * Erreurs 422 `validation_failed` (conventions §4.13.3) :
 * `{error: {details: {fields: [{field, code, message}, ...]}}}` → posées
 * inline via `aria-invalid` + `<p class="nm-field__error" role="alert">`.
 *
 * Statut (mode edit) : un changement du radio `statut` déclenche un appel
 * archive/restore *après* le PATCH (pas via PATCH `status`).
 *
 * Convention CLAUDE.md : aucun HTML inline JS — toutes les options du
 * select activité sont clonées depuis `<template id="tpl-activity-option">`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const CATEGORY_LABELS = {
  bic_vente: "BIC vente",
  bic_services: "BIC services",
  bnc: "BNC",
};

const CATEGORY_PILL_CLASSES = {
  bic_vente: "nm-pill--indigo",
  bic_services: "nm-pill--blue",
  bnc: "nm-pill--olive",
};

// Liste fermée des taux TVA quand l'organization est **assujettie**.
// Quand elle est en franchise (art. 293 B CGI), vatRate est envoyé `null`
// au backend et le select n'est pas affiché — cf. _applyVatVisibility().
const ALLOWED_VAT_RATES = new Set(["2.1", "5.5", "10", "20"]);

const FIELD_MAP = {
  label: { input: "labelInput", error: "labelError" },
  description: { input: "descriptionInput", error: "descriptionError" },
  reference: { input: "referenceInput", error: "referenceError" },
  unit: { input: "unitInput", error: "unitError" },
  priceExclVat: { input: "priceInput", error: "priceError" },
  vatRate: { input: "vatInput", error: "vatError" },
  activityId: { input: "activitySelect", error: "activityError" },
};

const PRICE_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default class ArticlesFormController extends BaseController {
  static targets = [
    "crumb", "overline", "title", "loadError",
    "archivedActivityBanner",
    "labelInput", "labelError",
    "activitySingleBlock", "activitySingleLabel",
    "activitySingleBadge", "activitySingleBadgeLabel",
    "activitySingleInput",
    "activityMultiBlock", "activitySelect",
    "activityMultiBadge", "activityMultiBadgeLabel",
    "activityError",
    "descriptionInput", "descriptionError",
    "referenceInput", "referenceError",
    "priceInput", "priceError",
    "unitInput", "unitError",
    "vatField", "vatInput", "vatError",
    "franchiseNotice",
    "previewLabel", "previewDescription", "previewLine", "previewTotal",
    "statusBlock", "statusActiveInput", "statusArchivedInput",
    "globalError", "actionsHint",
    "cancelButton", "submitButton",
  ];

  connect() {
    this._parseMode();
    this._activitiesById = new Map();
    this._activeActivities = [];
    this._currentActivity = null;
    this._article = null;
    this._initialStatus = "active";
    // Régime TVA : true par défaut (= en franchise) en attendant la
    // résolution de /auth/me, qui pilote l'affichage du select TVA
    // vs encart art. 293 B (cf. spec §4 *Champ TVA conditionnel*).
    // EPIC-019 alimentera la vraie valeur via organizations.vat_taxable_at.
    this._vatFranchise = true;
    this._applyVatVisibility();

    if (this._mode === "create") {
      if (this.hasOverlineTarget) this.overlineTarget.textContent = "Nouvel article";
      if (this.hasTitleTarget) this.titleTarget.textContent = "Nouvel article";
      if (this.hasCrumbTarget) this.crumbTarget.textContent = "Nouvel article";
      if (this.hasStatusBlockTarget) this.statusBlockTarget.hidden = true;
      if (this.hasCancelButtonTarget) this.cancelButtonTarget.setAttribute("href", "/catalogue.html");
      this._loadCreate();
    } else {
      if (this.hasOverlineTarget) this.overlineTarget.textContent = "Modification article";
      if (this.hasStatusBlockTarget) this.statusBlockTarget.hidden = false;
      this._loadEdit();
    }

    this._loadVatFranchiseStatus();
    this._wirePreview();
  }

  async _loadVatFranchiseStatus() {
    try {
      const me = await api.getMe();
      const ctx = me?.data ?? me;
      const flag = ctx?.organization?.vatFranchise;
      // L'absence du flag (organization null, ancien backend) garde le
      // comportement par défaut « en franchise ».
      this._vatFranchise = flag === false ? false : true;
      this._applyVatVisibility();
    } catch (_error) {
      // En cas d'erreur réseau, on garde le défaut (franchise) — le
      // backend re-validera de toute façon à la soumission.
    }
  }

  _applyVatVisibility() {
    if (this.hasVatFieldTarget) this.vatFieldTarget.hidden = this._vatFranchise;
    if (this.hasFranchiseNoticeTarget) this.franchiseNoticeTarget.hidden = !this._vatFranchise;
  }

  _parseMode() {
    // Forme canonique (EPIC-099.53) : articles-edition.html?id={uuid} en
    // édition, articles-edition.html sans paramètre en création.
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) {
      this._mode = "edit";
      this._articleId = id;
      return;
    }
    this._mode = "create";
    this._articleId = null;
  }

  // ===========================================================================
  // Chargement
  // ===========================================================================

  async _loadCreate() {
    try {
      const activitiesResp = await api.listActivities();
      this._cacheActivities(activitiesResp);
      this._setupActivitySelector(null);
      this._updatePreview();
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les activités.";
      this._showLoadError(msg);
    }
  }

  async _loadEdit() {
    try {
      const [articleResp, activitiesResp] = await Promise.all([
        api.getArticle(this._articleId),
        api.listActivities(),
      ]);
      this._cacheActivities(activitiesResp);
      this._article = articleResp?.data ?? articleResp;
      this._fillForm(this._article);
      this._setupActivitySelector(this._article);
      this._updatePreview();
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        this._showLoadError("Article introuvable.");
        return;
      }
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger l'article.";
      this._showLoadError(msg);
    }
  }

  _cacheActivities(response) {
    const list = Array.isArray(response?.activities)
      ? response.activities
      : Array.isArray(response?.data) ? response.data : [];
    for (const activity of list) {
      this._activitiesById.set(activity.id, activity);
    }
    this._activeActivities = list.filter((a) => a.status === "active");
  }

  _fillForm(article) {
    const label = article.label ?? "";
    if (this.hasLabelInputTarget) this.labelInputTarget.value = label;
    if (this.hasReferenceInputTarget) this.referenceInputTarget.value = article.reference ?? "";
    if (this.hasDescriptionInputTarget) this.descriptionInputTarget.value = article.description ?? "";
    if (this.hasPriceInputTarget) {
      this.priceInputTarget.value = this._formatPriceForInput(article.priceExclVat);
    }
    if (this.hasUnitInputTarget) this._selectByText(this.unitInputTarget, article.unit ?? "");
    if (this.hasVatInputTarget) {
      const value = this._normalizeVatValue(article.vatRate);
      if (value !== null) this.vatInputTarget.value = value;
    }

    this._initialStatus = article.status ?? "active";
    if (this._initialStatus === "archived" && this.hasStatusArchivedInputTarget) {
      this.statusArchivedInputTarget.checked = true;
    } else if (this.hasStatusActiveInputTarget) {
      this.statusActiveInputTarget.checked = true;
    }

    if (this.hasTitleTarget) this.titleTarget.textContent = label || "—";
    if (this.hasCrumbTarget) this.crumbTarget.textContent = label || "—";
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.setAttribute(
        "href",
        `/articles-detail.html?id=${encodeURIComponent(this._articleId)}`,
      );
    }
  }

  _normalizeVatValue(rate) {
    // Article en franchise (`vat_rate IS NULL` backend) → pas de valeur à
    // poser sur le select (qui sera de toute façon masqué par
    // _applyVatVisibility). Le select garde sa valeur par défaut (20 %).
    if (rate === null || rate === undefined) return null;
    const stripped = String(rate).replace(/0+$/, "").replace(/\.$/, "");
    if (ALLOWED_VAT_RATES.has(stripped)) return stripped;
    if (ALLOWED_VAT_RATES.has(String(rate))) return String(rate);
    // Cas exotique "10.00" ou "20.00"
    return stripped || null;
  }

  _selectByText(selectEl, text) {
    if (!selectEl || !text) return;
    const trimmed = text.trim();
    for (const option of Array.from(selectEl.options)) {
      const optionText = (option.value || option.textContent || "").trim();
      if (optionText === trimmed) {
        option.selected = true;
        return;
      }
    }
  }

  _formatPriceForInput(priceExclVat) {
    const n = Number(priceExclVat);
    if (!Number.isFinite(n)) return "";
    return PRICE_FORMATTER.format(n).replace(/ /g, " ");
  }

  _showLoadError(message) {
    if (this.hasLoadErrorTarget) {
      this.loadErrorTarget.textContent = message;
      this.loadErrorTarget.hidden = false;
    }
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
  }

  // ===========================================================================
  // Activity selector
  // ===========================================================================

  _setupActivitySelector(article) {
    const currentActivityId = article?.activityId ?? null;
    const currentActivity = currentActivityId ? this._activitiesById.get(currentActivityId) ?? null : null;
    const currentArchived = currentActivity?.status === "archived";
    this._currentActivity = currentActivity;

    // Banner activité archivée — uniquement en mode edit.
    if (this.hasArchivedActivityBannerTarget) {
      this.archivedActivityBannerTarget.hidden = !(this._mode === "edit" && currentArchived);
    }

    const actives = this._activeActivities;

    // Cas drop-down forcée : édition avec activité courante archivée.
    if (this._mode === "edit" && currentArchived) {
      this._renderMultiSelect(actives, null);
      return;
    }

    // Cas N=1 : champ caché + libellé + badge.
    if (actives.length === 1 && (this._mode === "create" || currentActivityId === actives[0].id)) {
      this._renderSingle(actives[0]);
      return;
    }

    // Cas N=0 (édition d'un article avec activité disparue, ou aucune
    // activité dans l'org) — on affiche quand même le select (vide) pour
    // signaler le problème au lieu de planter.
    this._renderMultiSelect(actives, currentActivityId && !currentArchived ? currentActivityId : null);
  }

  _renderSingle(activity) {
    if (this.hasActivityMultiBlockTarget) this.activityMultiBlockTarget.hidden = true;
    if (!this.hasActivitySingleBlockTarget) return;
    this.activitySingleBlockTarget.hidden = false;
    if (this.hasActivitySingleLabelTarget) {
      this.activitySingleLabelTarget.textContent = activity.description || "—";
    }
    if (this.hasActivitySingleBadgeTarget) {
      this._applyCategoryPill(
        this.activitySingleBadgeTarget,
        this.activitySingleBadgeLabelTarget,
        activity.category,
      );
    }
    if (this.hasActivitySingleInputTarget) {
      this.activitySingleInputTarget.value = activity.id;
    }
  }

  _renderMultiSelect(actives, preselectId) {
    if (this.hasActivitySingleBlockTarget) this.activitySingleBlockTarget.hidden = true;
    if (!this.hasActivityMultiBlockTarget || !this.hasActivitySelectTarget) return;
    this.activityMultiBlockTarget.hidden = false;

    while (this.activitySelectTarget.firstChild) {
      this.activitySelectTarget.removeChild(this.activitySelectTarget.firstChild);
    }

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.disabled = true;
    placeholder.textContent = "Choisir une activité…";
    placeholder.selected = !preselectId;
    this.activitySelectTarget.appendChild(placeholder);

    for (const activity of actives) {
      const fragment = this.cloneTemplate("tpl-activity-option");
      const option = fragment.querySelector("option");
      if (!option) continue;
      option.value = activity.id;
      option.textContent = `${activity.description || "—"} (${CATEGORY_LABELS[activity.category] ?? activity.category})`;
      option.dataset.category = activity.category ?? "";
      if (preselectId === activity.id) option.selected = true;
      this.activitySelectTarget.appendChild(option);
    }

    this._refreshMultiBadge();
  }

  _refreshMultiBadge() {
    if (!this.hasActivitySelectTarget || !this.hasActivityMultiBadgeTarget) return;
    const selected = this.activitySelectTarget.selectedOptions[0];
    const category = selected?.dataset.category || null;
    this._applyCategoryPill(
      this.activityMultiBadgeTarget,
      this.activityMultiBadgeLabelTarget,
      category,
    );
  }

  _applyCategoryPill(pill, labelEl, category) {
    if (!pill) return;
    Object.values(CATEGORY_PILL_CLASSES).forEach((cls) => pill.classList.remove(cls));
    if (category && CATEGORY_PILL_CLASSES[category]) {
      pill.classList.add(CATEGORY_PILL_CLASSES[category]);
      pill.hidden = false;
      if (labelEl) labelEl.textContent = CATEGORY_LABELS[category] ?? category;
    } else {
      pill.hidden = true;
    }
  }

  changeActivity() {
    this._refreshMultiBadge();
    this._updatePreview();
  }

  changeVat() {
    this._updatePreview();
  }

  // ===========================================================================
  // Preview
  // ===========================================================================

  _wirePreview() {
    if (this.hasLabelInputTarget) {
      this.labelInputTarget.addEventListener("input", () => this._updatePreview());
    }
    if (this.hasDescriptionInputTarget) {
      this.descriptionInputTarget.addEventListener("input", () => this._updatePreview());
    }
    if (this.hasPriceInputTarget) {
      this.priceInputTarget.addEventListener("input", () => this._updatePreview());
    }
    if (this.hasUnitInputTarget) {
      this.unitInputTarget.addEventListener("change", () => this._updatePreview());
    }
  }

  _updatePreview() {
    const label = this._read(this.hasLabelInputTarget ? this.labelInputTarget : null);
    const description = this._read(this.hasDescriptionInputTarget ? this.descriptionInputTarget : null);
    const unit = this.hasUnitInputTarget
      ? (this.unitInputTarget.selectedOptions[0]?.value || this.unitInputTarget.selectedOptions[0]?.textContent || "").trim()
      : "";
    const priceStr = this._read(this.hasPriceInputTarget ? this.priceInputTarget : null);
    const price = this._parsePrice(priceStr);

    if (this.hasPreviewLabelTarget) this.previewLabelTarget.textContent = label || "—";
    if (this.hasPreviewDescriptionTarget) {
      this.previewDescriptionTarget.textContent = description || "—";
    }
    if (this.hasPreviewLineTarget) {
      const priceLabel = price !== null ? `${PRICE_FORMATTER.format(price)} €` : "—";
      this.previewLineTarget.textContent = `1 ${unit || "—"} × ${priceLabel}`;
    }
    if (this.hasPreviewTotalTarget) {
      const total = price !== null ? `${PRICE_FORMATTER.format(price)} € HT` : "— € HT";
      this.previewTotalTarget.textContent = total;
    }
  }

  // ===========================================================================
  // Submit
  // ===========================================================================

  async submit(event) {
    event?.preventDefault?.();
    this._clearAllErrors();

    const payload = this._collectPayload();
    if (!payload) return;

    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    try {
      if (this._mode === "create") {
        const response = await api.createArticle(payload);
        const article = response?.data ?? response;
        this.showSuccess("Article créé.");
        window.location.assign(`/articles-detail.html?id=${encodeURIComponent(article.id)}`);
        return;
      }
      // mode edit
      const response = await api.updateArticle(this._articleId, payload);
      const article = response?.data ?? response;

      // Synchronisation du statut si modifié par l'utilisateur.
      const desiredStatus = this._readStatusRadio();
      if (desiredStatus && desiredStatus !== article.status) {
        try {
          const statusResp = desiredStatus === "archived"
            ? await api.archiveArticle(this._articleId)
            : await api.restoreArticle(this._articleId);
          this._article = statusResp?.data ?? statusResp;
        } catch (statusError) {
          if (this._handleStatusError(statusError)) {
            // L'erreur statut est gérée (modale blocked ou inline) ; on
            // reste sur la page d'édition pour laisser l'utilisateur corriger.
            return;
          }
          throw statusError;
        }
      }

      this.showSuccess("Article enregistré.");
      window.location.assign(`/articles-detail.html?id=${encodeURIComponent(this._articleId)}`);
    } catch (error) {
      this._handleSubmitError(error);
    } finally {
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
    }
  }

  _collectPayload() {
    const errors = [];
    const label = this._read(this.labelInputTarget);
    if (!label) {
      errors.push({ field: "label", message: "Désignation obligatoire." });
    } else if (label.length > 500) {
      errors.push({ field: "label", message: "Maximum 500 caractères." });
    }

    const description = this._read(this.descriptionInputTarget);
    if (description.length > 10000) {
      errors.push({ field: "description", message: "Maximum 10000 caractères." });
    }

    const reference = this.hasReferenceInputTarget
      ? this._read(this.referenceInputTarget)
      : "";
    if (reference.length > 64) {
      errors.push({ field: "reference", message: "Maximum 64 caractères." });
    }

    const unit = this.hasUnitInputTarget
      ? (this.unitInputTarget.selectedOptions[0]?.value || this.unitInputTarget.selectedOptions[0]?.textContent || "").trim()
      : "";
    if (!unit) {
      errors.push({ field: "unit", message: "Unité obligatoire." });
    }

    const priceRaw = this._read(this.priceInputTarget);
    const price = this._parsePrice(priceRaw);
    if (price === null) {
      errors.push({ field: "priceExclVat", message: "Prix invalide (≥ 0, ≤ 2 décimales)." });
    }

    // Régime TVA : si l'organization est en franchise, on envoie
    // `vatRate: null` (le backend valide `not_applicable_in_franchise`
    // si une valeur arrive). Sinon, validation stricte de la liste fermée.
    let vatRate = null;
    if (!this._vatFranchise) {
      const raw = this.hasVatInputTarget ? this.vatInputTarget.value : "";
      if (!ALLOWED_VAT_RATES.has(raw)) {
        errors.push({ field: "vatRate", message: "Taux TVA invalide." });
      } else {
        vatRate = raw;
      }
    }

    const activityId = this._readActivityId();
    if (!activityId) {
      errors.push({ field: "activityId", message: "Activité obligatoire." });
    }

    if (errors.length > 0) {
      this._renderFieldErrors(errors);
      return null;
    }

    const payload = {
      label,
      unit,
      priceExclVat: price.toFixed(this._priceDecimals(priceRaw)),
      vatRate,
      activityId,
    };
    if (description) payload.description = description;
    // Référence : en édition, envoyer null pour effacer (trim serveur).
    // En création, omettre si vide pour rester compact.
    if (this.hasReferenceInputTarget) {
      if (this._mode === "edit") {
        payload.reference = reference === "" ? null : reference;
      } else if (reference !== "") {
        payload.reference = reference;
      }
    }
    return payload;
  }

  _priceDecimals(raw) {
    const normalized = raw.replace(/\s/g, "").replace(",", ".");
    const dot = normalized.indexOf(".");
    if (dot < 0) return 0;
    return Math.min(2, normalized.length - dot - 1);
  }

  _parsePrice(raw) {
    if (raw === null || raw === undefined) return null;
    const cleaned = String(raw).replace(/\s/g, "").replace(",", ".");
    if (cleaned === "") return null;
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
  }

  _readActivityId() {
    if (this.hasActivityMultiBlockTarget && !this.activityMultiBlockTarget.hidden) {
      return this.hasActivitySelectTarget ? this.activitySelectTarget.value : "";
    }
    if (this.hasActivitySingleBlockTarget && !this.activitySingleBlockTarget.hidden) {
      return this.hasActivitySingleInputTarget ? this.activitySingleInputTarget.value : "";
    }
    return "";
  }

  _readStatusRadio() {
    if (this._mode !== "edit") return null;
    if (this.hasStatusArchivedInputTarget && this.statusArchivedInputTarget.checked) return "archived";
    if (this.hasStatusActiveInputTarget && this.statusActiveInputTarget.checked) return "active";
    return null;
  }

  _read(input) {
    if (!input) return "";
    return (input.value ?? "").trim();
  }

  // ===========================================================================
  // Erreurs
  // ===========================================================================

  _handleSubmitError(error) {
    if (!(error instanceof APIError)) {
      this._showGlobalError("Erreur réseau, réessayez.");
      return;
    }
    if (error.status === 422) {
      const details = error.data?.error?.details;
      const fields = Array.isArray(details?.fields) ? details.fields : null;
      if (fields && fields.length > 0) {
        this._renderFieldErrors(fields);
        return;
      }
      if (details?.code === "article_activity_archived") {
        this._setFieldError("activityId", "L'activité associée est archivée. Réaffectez vers une activité active.");
        return;
      }
      this._showGlobalError(error.message || "Validation échouée.");
      return;
    }
    if (error.status === 404) {
      this._showGlobalError("Article introuvable.");
      return;
    }
    this._showGlobalError(error.message || "Erreur serveur, réessayez.");
  }

  _handleStatusError(error) {
    if (!(error instanceof APIError) || error.status !== 422) return false;
    const details = error.data?.error?.details;
    if (details?.code === "blocked_by_recurring_invoices") {
      const count = Number(details.recurringInvoiceCount) || 0;
      this._showGlobalError(
        `Archivage impossible : article utilisé par ${count} facturation(s) récurrente(s) active(s) ou en pause.`,
      );
      // Re-cocher le radio « Actif » pour refléter l'état réel.
      if (this.hasStatusActiveInputTarget) this.statusActiveInputTarget.checked = true;
      return true;
    }
    return false;
  }

  _renderFieldErrors(fields) {
    let firstInput = null;
    for (const item of fields) {
      const config = FIELD_MAP[item.field];
      if (!config) continue;
      this._setFieldErrorByConfig(config, item.message);
      if (!firstInput) {
        const target = this[`${config.input}Target`];
        if (target) firstInput = target;
      }
    }
    if (firstInput && typeof firstInput.focus === "function") {
      firstInput.focus();
    }
  }

  _setFieldError(fieldName, message) {
    const config = FIELD_MAP[fieldName];
    if (config) this._setFieldErrorByConfig(config, message);
  }

  _setFieldErrorByConfig(config, message) {
    const errorTarget = this[`${config.error}Target`];
    const inputTarget = this[`${config.input}Target`];
    if (errorTarget) {
      errorTarget.textContent = message ?? "Champ invalide.";
      errorTarget.hidden = false;
    }
    if (inputTarget && typeof inputTarget.setAttribute === "function") {
      inputTarget.setAttribute("aria-invalid", "true");
    }
  }

  _clearAllErrors() {
    for (const { input, error } of Object.values(FIELD_MAP)) {
      const errorTarget = this[`${error}Target`];
      const inputTarget = this[`${input}Target`];
      if (errorTarget) {
        errorTarget.textContent = "";
        errorTarget.hidden = true;
      }
      if (inputTarget && typeof inputTarget.removeAttribute === "function") {
        inputTarget.removeAttribute("aria-invalid");
      }
    }
    if (this.hasGlobalErrorTarget) {
      this.globalErrorTarget.textContent = "";
      this.globalErrorTarget.hidden = true;
    }
  }

  _showGlobalError(message) {
    if (!this.hasGlobalErrorTarget) {
      this.showError(message);
      return;
    }
    this.globalErrorTarget.textContent = message;
    this.globalErrorTarget.hidden = false;
  }
}
